import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { Permissions } from 'app/constants/permissions'
import {
  ROLES_KEY,
  addRoleRequest,
  deleteRolesRequest,
  editRoleRequest,
  fetchRoles,
  mapRoleFields,
} from 'app/containers/administration/roleQueries'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { Button } from '@/ui/button'
import { Loader } from '@/ui'
import { toast } from '@/lib/toasts/api'
import { RoleRow } from './components/RoleRow'

/**
 * Zakładka ROLE panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/roles.tsx` (klasa na `ValidatorBase`,
 * react-table v6). Zmiany uprawnień są lokalne do czasu kliknięcia „Zapisz", tak jak
 * dotąd; usuwanie idzie przez `adminSelectionStore` i kosz w pasku panelu.
 */
const AdminRolesPageContent: React.FC = () => {
  const { t } = useLocalize()
  const queryClient = useQueryClient()
  const { selectedGroup, resetAll } = useTable()

  const { data: savedRoles = [], isLoading } = useQuery({
    queryKey: ROLES_KEY,
    queryFn: fetchRoles,
  })

  // Robocza kopia - uprawnienia przełącza się na pigułkach, a zapis leci dopiero z przycisku.
  const [draft, setDraft] = React.useState<RolePermissionModel[] | null>(null)
  const roles = draft ?? savedRoles
  const isDirty = draft !== null

  const [newRoleName, setNewRoleName] = React.useState('')
  const [nameError, setNameError] = React.useState('')

  const invalidate = React.useCallback(
    () => queryClient.invalidateQueries({ queryKey: ROLES_KEY }),
    [queryClient],
  )

  const addMutation = useMutation({ mutationFn: addRoleRequest })
  const editMutation = useMutation({ mutationFn: editRoleRequest })
  const deleteMutation = useMutation({ mutationFn: deleteRolesRequest })

  const selectedCount = selectedGroup?.ids.length ?? 0
  React.useEffect(() => {
    setAdminSelectedCount(selectedCount)
  }, [selectedCount])
  React.useEffect(() => () => clearAdminSelection(), [])

  const removeRoles = React.useCallback(
    async (ids: Array<string | number>) => {
      if (ids.length === 0) return
      // Role systemowe są nieusuwalne - stara zakładka pokazywała wtedy ostrzeżenie.
      const system = roles.filter(
        (role) => ids.includes(role.id) && role.systemRole,
      )
      if (system.length > 0) {
        toast.warning({
          title: t('administrationPanel-roles.role-not-edited'),
          text: system.map((role) => role.name).join(', '),
        })
        return
      }
      await deleteMutation.mutateAsync(ids.map(String))
      await invalidate()
      setDraft(null)
      toast.success({ title: t('administrationPanel-roles.role-deleted') })
    },
    [roles, deleteMutation, invalidate, t],
  )

  const selectedRef = React.useRef<Array<string | number>>([])
  selectedRef.current = selectedGroup?.ids ?? []
  React.useEffect(
    () =>
      subscribeToAdminRemove(async () => {
        await removeRoles(selectedRef.current)
        resetAll()
        clearAdminSelection()
      }),
    [removeRoles, resetAll],
  )

  const togglePermission = (role: RolePermissionModel, permission: string) => {
    if (role.systemRole && permission === Permissions.isDriver) {
      toast.warning({
        title: t('administrationPanel-roles.role-not-edited'),
        text: role.name,
      })
      return
    }
    setDraft(
      roles.map((current) =>
        current.id === role.id
          ? { ...current, [permission]: !current[permission] }
          : current,
      ),
    )
  }

  const addRole = async () => {
    if (!newRoleName.trim()) {
      setNameError(t('validation.required'))
      return
    }
    setNameError('')
    const response = await addMutation.mutateAsync({
      name: newRoleName,
    } as RolePermissionModel)
    const added = mapRoleFields((response as any).content?.data ?? response)
    if (added?.id) {
      setNewRoleName('')
      await invalidate()
      setDraft(null)
      toast.success({ title: t('administrationPanel-roles.role-added') })
    } else {
      toast.error({ title: t('administrationPanel-roles.role-not-added') })
    }
  }

  const save = async () => {
    await editMutation.mutateAsync(roles)
    await invalidate()
    setDraft(null)
    toast.success({
      title: t('administrationPanel-roles.role-edited-title'),
      text: t('administrationPanel-roles.role-edited'),
    })
  }

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <input
          className="tw-w-[280px] tw-rounded tw-border tw-border-base-300 tw-px-3 tw-py-1.5 tw-text-sm tw-outline-none focus:tw-border-primary"
          placeholder={t('administrationPanel-roles.role-name')}
          value={newRoleName}
          onChange={(event) => setNewRoleName(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && addRole()}
        />
        <Button
          variant="primary"
          size="sm"
          disabled={addMutation.isPending}
          onClick={addRole}
        >
          <AdminIcon name="add" className="white tw-h-4 tw-w-4" />
          {t('administrationPanel-roles.add-role')}
        </Button>
        {nameError && (
          <span className="tw-text-xs tw-text-error">{nameError}</span>
        )}
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-content-primary">
            <tr>
              <th className="tw-w-12 tw-border-b tw-border-base-300" />
              <th className="tw-w-[250px] tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
                {t('administrationPanel-roles.role-name')}
              </th>
              <th className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
                {t('administrationPanel-roles.permissions')}
              </th>
              <th className="tw-w-12 tw-border-b tw-border-base-300" />
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <RoleRow
                key={role.id}
                role={role}
                onTogglePermission={togglePermission}
                onDelete={(current) => removeRoles([current.id])}
              />
            ))}
          </tbody>
        </table>

        {isLoading && (
          <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
            <Loader size="lg" />
          </div>
        )}
      </div>

      {isDirty && (
        <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-base-300 tw-px-4 tw-py-2">
          <Button
            variant="primary"
            size="sm"
            disabled={editMutation.isPending}
            onClick={save}
          >
            {t('operation.save')}
          </Button>
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            onClick={() => setDraft(null)}
          >
            {t('operation.cancel')}
          </Button>
        </div>
      )}
    </div>
  )
}

export const AdminRolesPage: React.FC = () => (
  <TableProvider>
    <AdminRolesPageContent />
  </TableProvider>
)
